// Region-edit instruction bar — after making a selection, type an instruction to edit just that region.
// Anchored near the selection (anchor computed by App), not fixed to the canvas bottom;
// when there is no anchor (geometry unavailable), falls back to bottom-center. Bar controls share one height.

import { useState } from 'react';
import { useT } from '../i18n';
import { CheckIcon, XIcon } from './icons';

export interface RegionComposerProps {
  busy: boolean;
  error: string | null;
  /** Selection anchor inside canvas-area (center x, selection top/bottom y, area size for clamping) */
  anchor?: { cx: number; top: number; bottom: number; areaWidth: number; areaHeight: number } | null;
  onSubmit: (instruction: string) => void;
  onCancel: () => void;
}

export default function RegionComposer({ busy, error, anchor, onSubmit, onCancel }: RegionComposerProps) {
  const t = useT();
  const [text, setText] = useState('');
  const submit = () => {
    const v = text.trim();
    if (!v || busy) return;
    onSubmit(v);
  };
  // Anchored mode: horizontally centered on the selection (clamped to the visible area); vertically placed
  // 10px below the selection bottom by default. If the selection sits near the canvas bottom and there is no
  // room below, flip above the selection; final clamp keeps the bar inside canvas-area.
  let style: React.CSSProperties | undefined;
  if (anchor) {
    const half = 220; // approximate half-width (conservative vs. bar max 680 / input min 260), for clamping
    const cx = Math.max(half, Math.min(anchor.areaWidth - half, anchor.cx));
    const GAP = 10;
    const BAR_HEIGHT = 56; // bar height estimate: controls 34 + padding 20 + border 2, for vertical clamping
    let top = anchor.bottom + GAP;
    if (top + BAR_HEIGHT > anchor.areaHeight - GAP) {
      // No room below: flip above the selection so the bar is not pushed out of the canvas area and clipped
      top = anchor.top - BAR_HEIGHT - GAP;
    }
    // Fallback clamp: keep the whole bar inside canvas-area even when the selection is huge or touches the top
    top = Math.max(GAP, Math.min(top, anchor.areaHeight - BAR_HEIGHT - GAP));
    style = { left: cx, top, bottom: 'auto', transform: 'translateX(-50%)' };
  }
  return (
    <div className="region-composer" style={style}>
      <input
        className="text-input region-composer-input"
        value={text}
        placeholder={t('region.placeholder')}
        disabled={busy}
        autoFocus
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') onCancel(); }}
      />
      <button className="primary-btn" onClick={submit} disabled={busy || !text.trim()}><CheckIcon /> {t('region.confirm')}</button>
      <button className="ghost-btn" onClick={onCancel} disabled={busy}><XIcon /> {t('region.cancel')}</button>
      {error && <p className="error-text region-composer-err">{error}</p>}
    </div>
  );
}
